import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { browserHistory } from "react-router";
import ImpersonateUser from "../../../../shared/ImpersonateUser";
import Loader from "../../../../common/Loader";

import StudentCoursesTable from "./courses/StudentCoursesTable";
import StudentLatestCourseDetails from "./courses/StudentLatestCourseDetails";
import StudentRewardsTable from "./rewards/StudentRewardsTable";

import EduApi from "../../../../../services/resources/EduApi";
import { ApiCallErrorMessageHandler } from "../../../../../lib/coc-common-scripts";
import AuthService, {
  PermissionClaims,
} from "../../../../../services/AuthService";
import { impersonationUserTypes } from "../../../../../lib";
import { EduProgramTypes } from "../../../EduConsts";
import queryString from "query-string";
import axios from "axios";
import { useState } from "react";

export default function StudentEnrollmentDetails(props) {
  const {
    location: { query, pathname },
    studentEnrollment,
    viewOnly,
  } = props;

  const studentEnrollmentId = studentEnrollment?.id;

  const [eduStudent, setEduStudent] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);

  const tabs = useMemo(
    () => [
      {
        component: StudentCoursesTable,
        id: "courses",
        name: "Course History",
      },
      {
        component: StudentRewardsTable,
        id: "rewards",
        name: "Reward History",
      },
    ],
    [],
  );

  const apiSignalRef = useRef(axios.CancelToken.source());

  useEffect(() => {
    const _apiSignal = apiSignalRef.current;
    return () => _apiSignal.cancel();
  }, []);

  const toTab = useCallback(
    (index) => {
      if (index !== tabIndex) {
        setTabIndex(index);

        const updatedQuery = { ...query };
        updatedQuery.sub = tabs[index].id;
        browserHistory.replace(
          `${pathname}?${queryString.stringify(updatedQuery)}`,
        );
      }
    },
    [tabIndex, pathname, query, tabs],
  );

  const getEduStudentDetails = useCallback(
    async (eduStudentEnrollmentId, doSetLoading = true) => {
      if (doSetLoading) {
        setLoading(true);
      }

      try {
        const eduStudent = await EduApi.getEduStudent(
          apiSignalRef.current.token,
          eduStudentEnrollmentId,
        );

        setEduStudent(eduStudent);
        setLoading(false);
      } catch (err) {
        if (!axios.isCancel(err)) {
          setErrorMessage(
            ApiCallErrorMessageHandler(
              err,
              "Sorry, something went wrong and student details could not be retrieved. Please try again.",
            ),
          );
          setLoading(false);
        }
      }
    },
    [],
  );

  useEffect(() => {
    const currentTabIndex = tabs
      .map((t) => t.id)
      .indexOf(decodeURIComponent(query.sub));
    toTab(currentTabIndex >= 0 ? currentTabIndex : 0);

    if (studentEnrollment) {
      getEduStudentDetails(studentEnrollment.id);
    }
  }, [studentEnrollment, getEduStudentDetails, query.sub, tabs, toTab]);

  useEffect(() => {
    if (studentEnrollmentId) {
      getEduStudentDetails(studentEnrollmentId);
    }
  }, [studentEnrollmentId, getEduStudentDetails]);

  const {
    eduProgramName,
    eduProgramType,
    status,
    student: { email } = {},
  } = eduStudent || {};

  return (
    <div>
      {" "}
      {loading ? (
        <Loader />
      ) : errorMessage || !eduStudent ? (
        <div className="full-page-error-text error-text">
          <img src="/img/error.svg" alt="error robot" height="240" />
          <p>
            {errorMessage ||
              `Sorry, something went wrong and enrollment details could not be retrieved.`}
          </p>
        </div>
      ) : (
        eduStudent && (
          <React.Fragment>
            {status === "Rejected" && (
              <div className="flex flex-justify-end mb-8">
                <p
                  className="medium-text rejected-chip"
                  style={{ padding: 14 }}
                >
                  Rejected from {eduProgramName}
                </p>
              </div>
            )}
            {eduProgramType === EduProgramTypes.YourIsrael &&
              AuthService.UserHasClaim(PermissionClaims.UserImpersonation) &&
              email && (
                <div className="flex flex-justify-end">
                  <ImpersonateUser
                    redirectUrlEnd="?after=yourisrael"
                    username={email}
                    userType={impersonationUserTypes.Student}
                  />
                </div>
              )}
            {eduProgramType !== EduProgramTypes.JewishU ? (
              <StudentLatestCourseDetails
                eduStudent={eduStudent}
                viewOnly={viewOnly}
              />
            ) : (
              <div className="flex mt-16" style={{ alignItems: "flex-start" }}>
                <div className="student-details-edu-tabs-card mb-16">
                  {tabs.map((tab, index) => (
                    <p
                      key={index}
                      className={`large-text link-text-secondary ${
                        index > 0 ? "ml-40" : ""
                      } ${tabIndex === index ? "active" : ""}`}
                      onClick={() => toTab(index)}
                    >
                      {tab.name}
                    </p>
                  ))}
                </div>
                <div className="full-width">
                  {React.createElement(tabs[tabIndex].component, {
                    eduStudent,
                    refreshEduStudent: () =>
                      getEduStudentDetails(
                        eduStudent.eduStudentEnrollmentID,
                        false,
                      ),
                    viewOnly,
                  })}
                </div>
              </div>
            )}
          </React.Fragment>
        )
      )}
    </div>
  );
}
